import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, authHeaders, errMessage, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatTile } from "@/components/StatTile";
import { PasswordInput } from "@/components/PasswordInput";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import { Shield, Loader2, Store, Users, Tag, Ban, Trash2, Search, LogOut, CheckCircle2, Flag, Mail, Star, Plus, Pause, Play, ScrollText, Download, BellRing, Bell, FileText, Copy, ChevronRight, Sparkles, RotateCcw, X, Smartphone, UploadCloud } from "lucide-react";
import { toast } from "sonner";

export default function AdminPanel() {
  const { admin, adminLoginToken, adminLogout, ready } = useAuth();
  if (!ready) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></Center>;
  return admin ? <AdminDash onLogout={adminLogout} /> : <AdminLogin onLogin={adminLoginToken} />;
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-50">{children}</div>;
}

function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await api.post("/admin/auth/login", {
        email: (form.email || "").trim().toLowerCase(),
        password: (form.password || "").trim(),
      });
      onLogin(r.data);
    } catch (err) { setError(errMessage(err, "Credenziali non valide")); }
    finally { setLoading(false); }
  };

  return (
    <Center>
      <form onSubmit={submit} className="w-full max-w-sm kn-card p-6 space-y-4" data-testid="admin-login-form">
        <div className="h-12 w-12 rounded-2xl bg-slate-900 flex items-center justify-center"><Shield className="h-6 w-6 text-white" /></div>
        <div><h1 className="text-xl font-extrabold text-slate-900">Pannello Admin</h1><p className="text-slate-500 text-sm">Accesso riservato agli amministratori.</p></div>
        <div className="space-y-1.5"><Label>Email</Label><Input data-testid="admin-email" type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" /></div>
        <div className="space-y-1.5"><Label>Password</Label><PasswordInput testId="admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" /></div>
        {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="admin-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="admin-submit" className="w-full h-11 rounded-xl font-bold">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accedi"}</Button>
      </form>
    </Center>
  );
}

function AdminDash({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [abuse, setAbuse] = useState([]);
  const [invites, setInvites] = useState([]);
  const [invitesConverted, setInvitesConverted] = useState(0);
  const [auditLog, setAuditLog] = useState([]);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [deletedProfiles, setDeletedProfiles] = useState([]);
  const [reviewDisputes, setReviewDisputes] = useState([]);
  const [couponAbuse, setCouponAbuse] = useState({ settings: { enabled: true, waste_threshold: 3, suspension_hours: 48 }, clients: [] });
  const [amountDisputes, setAmountDisputes] = useState([]);
  const [resolvedDisputes, setResolvedDisputes] = useState([]);
  const [disputeView, setDisputeView] = useState("open");
  const [onlyWrong, setOnlyWrong] = useState(false);
  const [abuseSettings, setAbuseSettings] = useState({ enabled: true, wrong_threshold: 3, dispute_threshold: 5, suspension_hours: 48 });
  const [savingAbuse, setSavingAbuse] = useState(false);
  const [amountStats, setAmountStats] = useState({ vendors: [], clients: [], totals: { edits: 0, disputes: 0, resolved: 0 } });
  const [resolveTarget, setResolveTarget] = useState(null); // dispute object
  const [resolveForm, setResolveForm] = useState({ amount_spent: "", discount_eur: "", note: "" });
  const [resolvingBusy, setResolvingBusy] = useState(false);
  const [receiptView, setReceiptView] = useState(null);
  const [aiTriaging, setAiTriaging] = useState(false);
  const [aiThreshold, setAiThreshold] = useState(() => {
    const v = parseFloat(localStorage.getItem("kn_ai_threshold"));
    return Number.isFinite(v) ? v : 0.8;
  });
  const [backingUp, setBackingUp] = useState(false);
  const [rejectVerifId, setRejectVerifId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectDocs, setRejectDocs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]);
  const [cats, setCats] = useState([]);
  const [catRequests, setCatRequests] = useState([]);
  const [catMacros, setCatMacros] = useState([]);
  const [catEdits, setCatEdits] = useState({});
  const [q, setQ] = useState("");
  const [vFilter, setVFilter] = useState({ city: "", category: "", blocked: "", sort: "recent" });
  const [offerOpen, setOfferOpen] = useState(false);
  const emptyOffer = { vendor_id: "", title: "", category: "", discount_type: "percent", discount_value: 20, min_spend: 0, quantity: "", duration: "today" };
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [savingOffer, setSavingOffer] = useState(false);
  const emptyDemo = { store_name: "", name: "", email: "", password: "", category: "ristoranti", phone: "" };
  const [demoType, setDemoType] = useState(null); // "vendor" | "client" | null
  const [demoForm, setDemoForm] = useState(emptyDemo);
  const [savingDemo, setSavingDemo] = useState(false);
  const [demoCreds, setDemoCreds] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [delTarget, setDelTarget] = useState(null); // { type, id, title }
  const [delReason, setDelReason] = useState("");
  const [delNote, setDelNote] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [tab, setTab] = useState("vendors");
  const [pwa, setPwa] = useState({ has_custom: false, version: 0, updated_at: null });
  const [pwaUploading, setPwaUploading] = useState(false);
  const [pwaIconKey, setPwaIconKey] = useState(0);
  const tabsRef = useRef(null);
  const goToTab = (t) => { setTab(t); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); };

  const load = useCallback(async () => {
    const vParams = { ...(q ? { q } : {}) };
    if (vFilter.city) vParams.city = vFilter.city;
    if (vFilter.category) vParams.category = vFilter.category;
    if (vFilter.blocked) vParams.blocked = vFilter.blocked;
    if (vFilter.sort) vParams.sort = vFilter.sort;
    const [s, v, c, rp, ab, iv, of, rv, al, da, vf, dp, rd, ca, ad, adr, ast] = await Promise.all([
      api.get("/admin/stats", { headers: authHeaders("admin") }),
      api.get("/admin/vendors", { headers: authHeaders("admin"), params: vParams }),
      api.get("/admin/clients", { headers: authHeaders("admin"), params: q ? { q } : {} }),
      api.get("/admin/reports", { headers: authHeaders("admin") }),
      api.get("/admin/abuse", { headers: authHeaders("admin") }),
      api.get("/admin/invites", { headers: authHeaders("admin") }),
      api.get("/admin/offers", { headers: authHeaders("admin"), params: q ? { q } : {} }),
      api.get("/admin/reviews", { headers: authHeaders("admin"), params: q ? { q } : {} }),
      api.get("/admin/audit-log", { headers: authHeaders("admin") }),
      api.get("/admin/deleted-accounts", { headers: authHeaders("admin") }),
      api.get("/admin/verifications", { headers: authHeaders("admin") }),
      api.get("/admin/deleted-profiles", { headers: authHeaders("admin") }),
      api.get("/admin/review-disputes", { headers: authHeaders("admin") }),
      api.get("/admin/coupon-abuse", { headers: authHeaders("admin") }),
      api.get("/admin/amount-disputes", { headers: authHeaders("admin"), params: { status: "open" } }),
      api.get("/admin/amount-disputes", { headers: authHeaders("admin"), params: { status: "resolved" } }),
      api.get("/admin/amount-stats", { headers: authHeaders("admin") }),
    ]);
    setStats(s.data); setVendors(v.data.vendors || []); setClients(c.data.clients || []);
    setReports(rp.data.reports || []); setAbuse((ab.data.clients || []).filter((x) => x.total_redeemed > 0)); setInvites(iv.data.invites || []);
    setInvitesConverted(iv.data.converted || 0);
    setOffers(of.data.offers || []); setAdminReviews(rv.data.reviews || []); setAuditLog(al.data.logs || []);
    setDeletedAccounts(da.data.accounts || []);
    setVerifications(vf.data.verifications || []);
    setDeletedProfiles(dp.data.profiles || []);
    setReviewDisputes(rd.data.disputes || []);
    setCouponAbuse(ca.data || { settings: {}, clients: [] });
    setAmountDisputes(ad.data.disputes || []);
    setResolvedDisputes(adr.data.disputes || []);
    setAmountStats(ast.data || { vendors: [], clients: [], totals: {} });
    if (ast.data?.abuse_settings) setAbuseSettings(ast.data.abuse_settings);
  }, [q, vFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/amount-disputes", { headers: authHeaders("admin"), params: { status: "open" } });
        setAmountDisputes(r.data.disputes || []);
      } catch (_) {}
    }, 25000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const loadCatRequests = useCallback(async () => {
    try {
      const r = await api.get("/admin/category-requests", { headers: authHeaders("admin"), params: { status: "pending" } });
      setCatRequests(r.data.requests || []);
      setCatMacros(r.data.macros || []);
    } catch (_) {}
  }, []);
  useEffect(() => { loadCatRequests(); }, [loadCatRequests]);

  const approveCat = async (req) => {
    const e = catEdits[req.request_id] || {};
    const label = (e.label != null && e.label.trim()) ? e.label.trim() : req.name;
    try {
      await api.post(`/admin/category-requests/${req.request_id}/approve`,
        { label, macro_id: e.macro_id || "altro" },
        { headers: authHeaders("admin") });
      toast.success(`Categoria «${label}» approvata`);
      loadCatRequests();
      api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    } catch (err) { toast.error("Approvazione non riuscita"); }
  };
  const rejectCat = async (req) => {
    try {
      await api.post(`/admin/category-requests/${req.request_id}/reject`, {}, { headers: authHeaders("admin") });
      toast(`Categoria «${req.name}» rifiutata`);
      loadCatRequests();
    } catch (err) { toast.error("Operazione non riuscita"); }
  };
  useEffect(() => { api.get("/admin/pwa", { headers: authHeaders("admin") }).then((r) => setPwa(r.data)).catch(() => {}); }, []);

  const uploadPwaIcon = async (file) => {
    if (!file) return;
    setPwaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/pwa/icon", fd, { headers: { ...authHeaders("admin"), "Content-Type": "multipart/form-data" } });
      setPwa((p) => ({ ...p, has_custom: true, version: r.data.version }));
      setPwaIconKey((k) => k + 1);
      toast.success("Icona dell'app aggiornata");
    } catch (e) {
      toast.error(errMessage(e, "Caricamento non riuscito"));
    } finally {
      setPwaUploading(false);
    }
  };

  const resetPwaIcon = async () => {
    try {
      const r = await api.post("/admin/pwa/reset", {}, { headers: authHeaders("admin") });
      setPwa((p) => ({ ...p, has_custom: false, version: r.data.version }));
      setPwaIconKey((k) => k + 1);
      toast.success("Ripristinata l'icona predefinita");
    } catch (e) {
      toast.error(errMessage(e, "Operazione non riuscita"));
    }
  };

  const createOffer = async () => {
    if (!offerForm.vendor_id) return toast.error("Scegli un negozio");
    if (!offerForm.title) return toast.error("Inserisci un titolo");
    if (!offerForm.category) return toast.error("Scegli una categoria");
    setSavingOffer(true);
    try {
      const days = offerForm.duration === "today" ? 0 : Number(offerForm.duration);
      const exp = new Date(); exp.setHours(23, 59, 59, 0); if (days) exp.setDate(exp.getDate() + days);
      await api.post("/admin/offers", {
        vendor_id: offerForm.vendor_id, title: offerForm.title, category: offerForm.category,
        discount_type: offerForm.discount_type, discount_value: Number(offerForm.discount_value),
        min_spend: Number(offerForm.min_spend || 0),
        quantity: offerForm.quantity === "" ? null : Number(offerForm.quantity),
        expires_at: exp.toISOString(),
      }, { headers: authHeaders("admin") });
      toast.success("Offerta creata");
      setOfferOpen(false); setOfferForm(emptyOffer); load();
    } catch (err) { toast.error(errMessage(err)); }
    finally { setSavingOffer(false); }
  };
  const toggleOffer = async (o) => {
    await api.put(`/admin/offers/${o.offer_id}`, { active: !o.active }, { headers: authHeaders("admin") });
    load();
  };
  const deleteOffer = async (o) => {
    if (!window.confirm("Eliminare questa offerta?")) return;
    await api.delete(`/admin/offers/${o.offer_id}`, { headers: authHeaders("admin") });
    toast.success("Offerta eliminata"); load();
  };
  const deleteReview = async (r) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    await api.delete(`/admin/reviews/${r.review_id}`, { headers: authHeaders("admin") });
    toast.success("Recensione eliminata"); load();
  };
  const resolveDispute = async (d, action) => {
    const msg = action === "remove" ? "Rimuovere la recensione segnalata?" : "Confermare la recensione come valida?";
    if (!window.confirm(msg)) return;
    await api.post(`/admin/review-disputes/${d.dispute_id}/resolve`, { action }, { headers: authHeaders("admin") });
    toast.success(action === "remove" ? "Recensione rimossa" : "Recensione confermata"); load();
  };
  const openResolve = (d, prefill) => {
    setResolveTarget(d);
    setResolveForm({
      amount_spent: prefill != null ? String(prefill) : String(d.amount_spent ?? ""),
      discount_eur: String(d.discount_eur ?? d.vendor_discount_eur ?? d.client_discount_eur ?? 0),
      note: "",
    });
  };
  const submitResolve = async () => {
    const amt = parseFloat(resolveForm.amount_spent);
    const disc = parseFloat(resolveForm.discount_eur) || 0;
    if (isNaN(amt) || amt < 0) return toast.error("Inserisci un importo valido");
    setResolvingBusy(true);
    try {
      await api.post(`/admin/amount-disputes/${resolveTarget.claim_id}/resolve`,
        { amount_spent: amt, discount_eur: disc, note: resolveForm.note }, { headers: authHeaders("admin") });
      toast.success("Contestazione risolta");
      setResolveTarget(null); load();
    } catch (err) { toast.error(errMessage(err)); }
    finally { setResolvingBusy(false); }
  };
  const saveAbuseSettings = async () => {
    setSavingAbuse(true);
    try {
      const r = await api.put("/admin/amount-abuse", abuseSettings, { headers: authHeaders("admin") });
      setAbuseSettings(r.data.settings);
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(errMessage(err)); }
    finally { setSavingAbuse(false); }
  };
  const revokeAmountSuspension = async (partyType, id) => {
    try {
      await api.post(`/admin/amount-abuse/${partyType}/${id}/revoke`, {}, { headers: authHeaders("admin") });
      toast.success("Sospensione revocata"); load();
    } catch (err) { toast.error(errMessage(err)); }
  };

  const runAiTriage = async () => {
    setAiTriaging(true);
    try {
      const r = await api.post("/admin/review-disputes/ai-triage", { threshold: aiThreshold }, { headers: authHeaders("admin") });
      const { analyzed, auto_removed, auto_kept, escalated, remaining } = r.data;
      toast.success(`AI: ${analyzed} analizzate — ${auto_removed} rimosse, ${auto_kept} mantenute, ${escalated} da rivedere${remaining ? ` · ${remaining} in coda` : ""}`);
      load();
    } catch (err) { toast.error(errMessage(err, "Analisi AI non riuscita")); }
    finally { setAiTriaging(false); }
  };
  const restoreDispute = async (d) => {
    if (!window.confirm("Ripristinare questa recensione rimossa?")) return;
    await api.post(`/admin/review-disputes/${d.dispute_id}/restore`, {}, { headers: authHeaders("admin") });
    toast.success("Recensione ripristinata"); load();
  };
  const saveCouponAbuse = async (patch) => {
    try {
      const r = await api.put("/admin/coupon-abuse", patch, { headers: authHeaders("admin") });
      setCouponAbuse((ca) => ({ ...ca, settings: r.data.settings }));
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(errMessage(err)); }
  };
  const revokeCouponSuspension = async (clientId) => {
    if (!window.confirm("Revocare la sospensione coupon per questo cliente?")) return;
    await api.post(`/admin/coupon-abuse/${clientId}/revoke`, {}, { headers: authHeaders("admin") });
    toast.success("Sospensione revocata"); load();
  };
  const createDemo = async () => {
    if (!demoForm.email) return toast.error("Inserisci una email");
    setSavingDemo(true);
    try {
      const email = demoForm.email.trim().toLowerCase();
      const pwd = (demoForm.password || "").trim() || null;
      if (demoType === "vendor") {
        const r = await api.post("/admin/vendors", {
          store_name: demoForm.store_name, email, password: pwd, category: demoForm.category,
        }, { headers: authHeaders("admin") });
        setDemoCreds({ type: "vendor", email, password: r.data.temp_password, loginUrl: "/vendor/login" });
      } else {
        const r = await api.post("/admin/clients", {
          name: demoForm.name, email, password: pwd, phone: demoForm.phone,
        }, { headers: authHeaders("admin") });
        setDemoCreds({ type: "client", email, password: r.data.temp_password, loginUrl: "/" });
      }
      toast.success("Account demo creato");
      load();
    } catch (err) { toast.error(errMessage(err)); }
    finally { setSavingDemo(false); }
  };
  const copyCreds = () => {
    if (!demoCreds) return;
    navigator.clipboard?.writeText(`Email: ${demoCreds.email}\nPassword: ${demoCreds.password}`);
    toast.success("Credenziali copiate");
  };
  const allTags = cats.flatMap((m) => m.tags.map((t) => ({ ...t, macro: m.label })));


  const block = async (type, id, blocked) => {
    await api.post(`/admin/${type}/${id}/block`, { blocked }, { headers: authHeaders("admin") });
    toast.success(blocked ? "Account sospeso" : "Account riattivato");
    load();
  };
  const askDelete = (type, id, title) => {
    setDelTarget({ type, id, title: title || "" });
    setDelReason("");
    setDelNote("");
  };
  const confirmDelete = async () => {
    if (!delTarget) return;
    const finalReason = delReason === "Altro"
      ? delNote.trim()
      : [delReason, delNote.trim()].filter(Boolean).join(" — ");
    setDelBusy(true);
    try {
      await api.delete(`/admin/${delTarget.type}/${delTarget.id}`, {
        headers: authHeaders("admin"),
        data: { reason: finalReason },
      });
      toast.success("Eliminato · email di notifica inviata");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDelBusy(false);
    }
  };
  const setReportStatus = async (reportId, status) => {
    await api.post(`/admin/reports/${reportId}/status`, { status }, { headers: authHeaders("admin") });
    toast.success("Segnalazione aggiornata");
    load();
  };
  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const r = await api.get("/admin/backup/download", { headers: authHeaders("admin"), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kouponow-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
      load();
    } catch (_) { toast.error("Backup non riuscito"); }
    finally { setBackingUp(false); }
  };
  const restoreAccount = async (email) => {
    try {
      await api.post("/admin/deleted-accounts/restore", { email }, { headers: authHeaders("admin") });
      toast.success("Ri-registrazione sbloccata");
      load();
    } catch (_) { toast.error("Operazione non riuscita"); }
  };
  const approveVerif = async (vendorId) => {
    try {
      await api.post(`/admin/verifications/${vendorId}/approve`, {}, { headers: authHeaders("admin") });
      toast.success("Venditore approvato");
      load();
    } catch (_) { toast.error("Operazione non riuscita"); }
  };
  const rejectVerif = async (vendorId) => {
    setRejectReason(""); setRejectDocs([]); setRejectVerifId(vendorId);
  };
  const confirmRejectVerif = async () => {
    if (!rejectReason.trim()) { toast.error("Inserisci il motivo del rifiuto"); return; }
    try {
      await api.post(`/admin/verifications/${rejectVerifId}/reject`, { reason: rejectReason, documents_to_resubmit: rejectDocs }, { headers: authHeaders("admin") });
      toast.success("Verifica rifiutata");
      setRejectVerifId(null); load();
    } catch (_) { toast.error("Operazione non riuscita"); }
  };
  const viewDoc = async (vendorId, docType) => {
    try {
      const r = await api.get(`/admin/verifications/${vendorId}/document/${docType}`, { headers: authHeaders("admin"), responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (_) { toast.error("Documento non disponibile"); }
  };

  const statCards = stats ? [
    { label: "Negozi", value: stats.vendors, icon: Store, tab: "vendors" },
    { label: "Clienti", value: stats.clients, icon: Users, tab: "clients" },
    { label: "Offerte attive", value: stats.offers_active, icon: Tag, tab: "offers" },
    { label: "Coupon", value: stats.claims, icon: CheckCircle2, tab: "abuse" },
    { label: "Segnalazioni", value: stats.reports_open, icon: Flag, tab: "reports" },
    { label: "Negozi invitati", value: stats.invites, icon: Search, tab: "invites" },
  ] : [];

  const RSTATUS = { open: { l: "Aperta", c: "bg-amber-50 text-amber-700" }, reviewed: { l: "Gestita", c: "bg-emerald-50 text-emerald-700" }, dismissed: { l: "Respinta", c: "bg-slate-100 text-slate-500" } };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white px-4 sm:px-6 h-16 flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center"><Shield className="h-5 w-5" /></div>
        <div><p className="font-extrabold">KoupoNow Admin</p></div>
        <button onClick={() => goToTab("amount-disputes")} data-testid="admin-bell" className="ml-auto relative text-slate-300 hover:text-white p-1.5" title="Contestazioni da decidere">
          <Bell className="h-5 w-5" />
          {amountDisputes.length > 0 && (
            <span data-testid="admin-bell-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E63946] text-white text-[10px] font-extrabold flex items-center justify-center animate-pulse">{amountDisputes.length}</span>
          )}
        </button>
        <button onClick={downloadBackup} disabled={backingUp} data-testid="admin-backup" className="flex items-center gap-2 text-slate-300 hover:text-white font-semibold text-sm">
          {backingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Backup
        </button>
        <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 text-slate-300 hover:text-white font-semibold text-sm"><LogOut className="h-4 w-4" /> Esci</button>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {statCards.map((s) => (
            <StatTile key={s.label} testId={`admin-stat-${s.label}`} icon={s.icon} value={s.value} label={s.label}
              accent="text-[#E63946]" onClick={() => goToTab(s.tab)} />
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o email..." className="h-12 pl-11 rounded-xl" data-testid="admin-search" />
        </div>

        <Tabs value={tab} onValueChange={setTab} ref={tabsRef}>
          <TabsList className="flex w-full h-11 rounded-xl bg-slate-100 p-1 overflow-x-auto no-scrollbar">
            <TabsTrigger value="vendors" data-testid="admin-tab-vendors" className="rounded-lg font-bold flex-1 whitespace-nowrap">Venditori ({vendors.length})</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients" className="rounded-lg font-bold flex-1 whitespace-nowrap">Clienti ({clients.length})</TabsTrigger>
            <TabsTrigger value="stats" data-testid="admin-tab-stats" className="rounded-lg font-bold flex-1 whitespace-nowrap">Statistiche</TabsTrigger>
            <TabsTrigger value="offers" data-testid="admin-tab-offers" className="rounded-lg font-bold flex-1 whitespace-nowrap">Offerte ({offers.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="rounded-lg font-bold flex-1 whitespace-nowrap">Recensioni ({adminReviews.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports" className="rounded-lg font-bold flex-1 whitespace-nowrap">Segnalazioni ({reports.filter((r) => r.status === "open").length})</TabsTrigger>
            <TabsTrigger value="abuse" data-testid="admin-tab-abuse" className="rounded-lg font-bold flex-1 whitespace-nowrap">Anti-abuso</TabsTrigger>
            <TabsTrigger value="coupon-abuse" data-testid="admin-tab-coupon-abuse" className="rounded-lg font-bold flex-1 whitespace-nowrap">Abusi coupon</TabsTrigger>
            <TabsTrigger value="amount-disputes" data-testid="admin-tab-amount-disputes" className="rounded-lg font-bold flex-1 whitespace-nowrap">Contestazioni ({amountDisputes.length})</TabsTrigger>
            <TabsTrigger value="invites" data-testid="admin-tab-invites" className="rounded-lg font-bold flex-1 whitespace-nowrap">Inviti ({invites.length})</TabsTrigger>
            <TabsTrigger value="categories" data-testid="admin-tab-categories" className="rounded-lg font-bold flex-1 whitespace-nowrap">Categorie ({catRequests.length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit" className="rounded-lg font-bold flex-1 whitespace-nowrap">Registro</TabsTrigger>
            <TabsTrigger value="deleted" data-testid="admin-tab-deleted" className="rounded-lg font-bold flex-1 whitespace-nowrap">Eliminati ({deletedAccounts.length})</TabsTrigger>
            <TabsTrigger value="verifications" data-testid="admin-tab-verifications" className="rounded-lg font-bold flex-1 whitespace-nowrap">Verifiche ({verifications.filter((x) => x.status === "submitted").length})</TabsTrigger>
            <TabsTrigger value="app" data-testid="admin-tab-app" className="rounded-lg font-bold flex-1 whitespace-nowrap">App</TabsTrigger>
          </TabsList>

          <TabsContent value="vendors" className="mt-4 space-y-2">
            <button onClick={() => { setDemoForm({ ...emptyDemo }); setDemoCreds(null); setDemoType("vendor"); }} data-testid="admin-new-vendor" className="w-full h-11 rounded-xl kn-gradient text-white font-bold flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Nuovo negozio demo
            </button>
            <div className="flex flex-wrap gap-2">
              <Input value={vFilter.city} onChange={(e) => setVFilter({ ...vFilter, city: e.target.value })} placeholder="Filtra per città" className="h-10 rounded-xl flex-1 min-w-[130px]" data-testid="vfilter-city" />
              <Select value={vFilter.category || "all"} onValueChange={(v) => setVFilter({ ...vFilter, category: v === "all" ? "" : v })}>
                <SelectTrigger className="h-10 rounded-xl flex-1 min-w-[130px]" data-testid="vfilter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="all">Tutte le categorie</SelectItem>{allTags.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={vFilter.blocked || "all"} onValueChange={(v) => setVFilter({ ...vFilter, blocked: v === "all" ? "" : v })}>
                <SelectTrigger className="h-10 rounded-xl w-[120px]" data-testid="vfilter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="false">Attivi</SelectItem><SelectItem value="true">Bloccati</SelectItem></SelectContent>
              </Select>
              <Select value={vFilter.sort} onValueChange={(v) => setVFilter({ ...vFilter, sort: v })}>
                <SelectTrigger className="h-10 rounded-xl w-[120px]" data-testid="vfilter-sort"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="recent">Recenti</SelectItem><SelectItem value="name">Nome A-Z</SelectItem></SelectContent>
              </Select>
            </div>
            {vendors.map((v) => (
              <Row key={v.vendor_id} title={v.store_name || "(senza nome)"} code={v.public_code} sub={`${v.email} · ${v.offer_count} offerte · ${v.follower_count} follower · ha invitato ${v.invited_total}`}
                blocked={v.blocked} onOpen={() => setDetailUser({ type: "vendor", id: v.vendor_id })} onBlock={() => block("vendors", v.vendor_id, !v.blocked)} onDelete={() => askDelete("vendors", v.vendor_id, v.store_name || v.email)} testid={`admin-vendor-${v.vendor_id}`} />
            ))}
          </TabsContent>
          <TabsContent value="clients" className="mt-4 space-y-2">
            <button onClick={() => { setDemoForm({ ...emptyDemo }); setDemoCreds(null); setDemoType("client"); }} data-testid="admin-new-client" className="w-full h-11 rounded-xl kn-gradient text-white font-bold flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Nuovo cliente demo
            </button>
            {clients.map((c) => (
              <Row key={c.client_account_id} title={c.name || c.email} code={c.public_code} sub={`${c.email} · ${c.claims_count} coupon · ${c.following_count} seguiti · ha invitato ${c.invited_total}`}
                blocked={c.blocked} onOpen={() => setDetailUser({ type: "client", id: c.client_account_id })} onBlock={() => block("clients", c.client_account_id, !c.blocked)} onDelete={() => askDelete("clients", c.client_account_id, c.name || c.email)} testid={`admin-client-${c.client_account_id}`} />
            ))}
          </TabsContent>

          <TabsContent value="stats" className="mt-4">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="offers" className="mt-4 space-y-2">
            <button onClick={() => { setOfferForm(emptyOffer); setOfferOpen(true); }} data-testid="admin-new-offer" className="w-full h-11 rounded-xl kn-gradient text-white font-bold flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Nuova offerta di test
            </button>
            {offers.length === 0 && <Empty text="Nessuna offerta" />}
            {offers.map((o) => {
              const OSTATUS = { active: "bg-emerald-50 text-emerald-700", suspended: "bg-amber-50 text-amber-700", expired: "bg-slate-100 text-slate-500", archived: "bg-slate-100 text-slate-400" };
              return (
                <div key={o.offer_id} className="kn-card p-3.5 flex items-center gap-3" data-testid={`admin-offer-${o.offer_id}`}>
                  <div className="h-11 w-11 rounded-xl kn-gradient text-white flex items-center justify-center font-extrabold text-xs shrink-0">{o.discount_type === "percent" ? `-${o.discount_value || o.discount}%` : "€"}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-800 truncate">{o.title}</p>
                    <p className="text-xs text-slate-400 truncate">{o.store_name} · <span className={`px-1.5 py-0.5 rounded-full ${OSTATUS[o.status] || ""}`}>{o.status}</span> · {o.click_count} richieste</p>
                  </div>
                  {o.status !== "archived" && (
                    <button onClick={() => toggleOffer(o)} data-testid={`admin-offer-toggle-${o.offer_id}`} title={o.active ? "Sospendi" : "Riattiva"} className="h-9 w-9 rounded-lg flex items-center justify-center text-amber-600 hover:bg-amber-50">
                      {o.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                  )}
                  <button onClick={() => deleteOffer(o)} data-testid={`admin-offer-delete-${o.offer_id}`} title="Elimina" className="h-9 w-9 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="reviews" className="mt-4 space-y-2">
            {reviewDisputes.filter((d) => d.status === "open").length > 0 && (
              <div className="space-y-2 mb-4" data-testid="admin-review-disputes">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-extrabold text-amber-700 flex items-center gap-1.5"><Flag className="h-4 w-4" /> Recensioni segnalate ({reviewDisputes.filter((d) => d.status === "open").length})</p>
                  <button onClick={runAiTriage} disabled={aiTriaging} data-testid="ai-triage-btn"
                    className="h-9 px-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-sm flex items-center gap-1.5 disabled:opacity-60">
                    {aiTriaging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analizza con AI
                  </button>
                </div>
                <p className="text-xs text-slate-400">L'AI risolve i casi evidenti e lascia qui solo quelli dubbi da rivedere.</p>
                <div className="kn-card p-3 border border-violet-100 bg-violet-50/40" data-testid="ai-threshold-box">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-violet-800">Confidenza minima per decidere da sola</span>
                    <span className="text-sm font-extrabold text-violet-700" data-testid="ai-threshold-value">{Math.round(aiThreshold * 100)}%</span>
                  </div>
                  <Slider data-testid="ai-threshold-slider" className="mt-2.5" min={50} max={100} step={1}
                    value={[Math.round(aiThreshold * 100)]}
                    onValueChange={(v) => { const t = v[0] / 100; setAiThreshold(t); localStorage.setItem("kn_ai_threshold", String(t)); }} />
                  <p className="text-[11px] text-slate-400 mt-2">Più alta = l'AI decide da sola solo nei casi molto evidenti (più recensioni restano da rivedere). Più bassa = l'AI risolve più casi in autonomia.</p>
                </div>
                {reviewDisputes.filter((d) => d.status === "open").map((d) => (
                  <div key={d.dispute_id} className="kn-card p-3.5 border border-amber-200 bg-amber-50/50" data-testid={`admin-dispute-${d.dispute_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800 text-sm">{d.store_name}</span>
                      <span className="flex items-center gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < d.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Cliente: {d.client_name} · {d.client_email}</p>
                    {d.comment && <p className="text-sm text-slate-600 mt-1.5 bg-white rounded-lg p-2">"{d.comment}"</p>}
                    {d.reason && <p className="text-xs text-amber-700 mt-1.5"><b>Motivo negozio:</b> {d.reason}</p>}
                    {d.ai_recommendation && (
                      <div className={`mt-2 rounded-lg p-2 text-xs flex items-start gap-1.5 ${d.ai_recommendation === "remove" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`dispute-ai-${d.dispute_id}`}>
                        <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span><b>AI suggerisce: {d.ai_recommendation === "remove" ? "Rimuovi" : "Mantieni"}</b>{typeof d.ai_confidence === "number" ? ` (${Math.round(d.ai_confidence * 100)}%)` : ""}{d.ai_reasoning ? ` — ${d.ai_reasoning}` : ""}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {d.client_email && (
                        <a href={`mailto:${d.client_email}?subject=${encodeURIComponent("Verifica recensione KoupoNow")}&body=${encodeURIComponent("Ciao " + (d.client_name || "") + ",\n\nil negozio " + d.store_name + " ha richiesto una verifica della recensione che hai lasciato. Puoi confermarci se la tua recensione è corretta?\n\nGrazie,\nAssistenza KoupoNow")}`}
                          data-testid={`dispute-contact-${d.dispute_id}`} className="h-9 px-3 rounded-xl bg-slate-800 text-white font-bold text-sm flex items-center gap-1.5"><Mail className="h-4 w-4" /> Contatta</a>
                      )}
                      <button onClick={() => resolveDispute(d, "keep")} data-testid={`dispute-keep-${d.dispute_id}`} className="h-9 px-3 rounded-xl bg-emerald-600 text-white font-bold text-sm">Conferma valida</button>
                      <button onClick={() => resolveDispute(d, "remove")} data-testid={`dispute-remove-${d.dispute_id}`} className="h-9 px-3 rounded-xl bg-red-600 text-white font-bold text-sm">Rimuovi</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {reviewDisputes.filter((d) => d.status === "resolved" && d.ai_recommendation).length > 0 && (
              <div className="space-y-2 mb-4" data-testid="admin-ai-resolved-disputes">
                <p className="font-extrabold text-violet-700 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Gestite dall'AI ({reviewDisputes.filter((d) => d.status === "resolved" && d.ai_recommendation).length})</p>
                {reviewDisputes.filter((d) => d.status === "resolved" && d.ai_recommendation).map((d) => (
                  <div key={d.dispute_id} className="kn-card p-3.5 border border-violet-100" data-testid={`ai-resolved-${d.dispute_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800 text-sm">{d.store_name}</span>
                      <span className="flex items-center gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < d.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${d.resolution === "remove" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{d.resolution === "remove" ? "Rimossa" : "Mantenuta"}</span>
                    </div>
                    {d.comment && <p className="text-sm text-slate-600 mt-1.5 bg-slate-50 rounded-lg p-2">"{d.comment}"</p>}
                    {d.ai_reasoning && <p className="text-xs text-violet-700 mt-1.5"><b>AI</b>{typeof d.ai_confidence === "number" ? ` (${Math.round(d.ai_confidence * 100)}%)` : ""}: {d.ai_reasoning}</p>}
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {d.resolution === "remove" ? (
                        <button onClick={() => restoreDispute(d)} data-testid={`dispute-restore-${d.dispute_id}`} className="h-9 px-3 rounded-xl bg-slate-800 text-white font-bold text-sm flex items-center gap-1.5"><RotateCcw className="h-4 w-4" /> Ripristina</button>
                      ) : (
                        <button onClick={() => resolveDispute(d, "remove")} data-testid={`dispute-override-remove-${d.dispute_id}`} className="h-9 px-3 rounded-xl bg-red-600 text-white font-bold text-sm">Rimuovi comunque</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {adminReviews.length === 0 && <Empty text="Nessuna recensione" />}
            {adminReviews.map((r) => (
              <div key={r.review_id} className="kn-card p-3.5 flex items-start gap-3" data-testid={`admin-review-${r.review_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-800 text-sm">{r.store_name || r.vendor_id}</span>
                    <span className="flex items-center gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className={`h-3.5 w-3.5 ${s < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{r.client_name}</p>
                  {r.comment && <p className="text-sm text-slate-600 mt-1.5 bg-slate-50 rounded-lg p-2">{r.comment}</p>}
                </div>
                <button onClick={() => deleteReview(r)} data-testid={`admin-review-delete-${r.review_id}`} title="Elimina" className="h-9 w-9 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="reports" className="mt-4 space-y-2">
            {reports.length === 0 && <Empty text="Nessuna segnalazione" />}
            {reports.map((r) => {
              const st = RSTATUS[r.status] || RSTATUS.open;
              return (
                <div key={r.report_id} className="kn-card p-3.5" data-testid={`admin-report-${r.report_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                    <span className="font-bold text-slate-800 text-sm">{REPORT_LABELS[r.reason] || r.reason}</span>
                    {r.vendor_open_reports > 1 && <span className="text-[11px] font-bold text-red-600">⚠ {r.vendor_open_reports} aperte su questo negozio</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{r.vendor_email}{r.vendor_phone ? ` · ${r.vendor_phone}` : ""}</p>
                  {r.note && <p className="text-sm text-slate-600 mt-1.5 bg-slate-50 rounded-lg p-2">{r.note}</p>}
                  <div className="flex gap-2 mt-2.5">
                    <button onClick={() => setReportStatus(r.report_id, "reviewed")} data-testid={`report-review-${r.report_id}`} className="h-8 px-3 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-xs">Segna gestita</button>
                    <button onClick={() => setReportStatus(r.report_id, "dismissed")} className="h-8 px-3 rounded-lg bg-slate-100 text-slate-600 font-bold text-xs">Respingi</button>
                    <button onClick={() => block("vendors", r.vendor_id, !r.vendor_blocked)} className="h-8 px-3 rounded-lg bg-red-50 text-red-600 font-bold text-xs ml-auto">{r.vendor_blocked ? "Sblocca negozio" : "Blocca negozio"}</button>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="abuse" className="mt-4 space-y-2">
            {abuse.length === 0 && <Empty text="Nessun dato di riscatto" />}
            {abuse.map((a) => (
              <div key={a.client_account_id} className={`kn-card p-3.5 flex items-center gap-3 ${a.suspicious ? "ring-2 ring-red-200" : ""}`} data-testid={`admin-abuse-${a.client_account_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 truncate flex items-center gap-2">
                    {a.name || a.email} {a.suspicious && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Sospetto</span>}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{a.email} · oggi {a.today} · max/giorno {a.max_day} · totale {a.total_redeemed}</p>
                </div>
                <button onClick={() => block("clients", a.client_account_id, !a.blocked)} className={`h-9 w-9 rounded-lg flex items-center justify-center ${a.blocked ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}>
                  {a.blocked ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="coupon-abuse" className="mt-4 space-y-4">
            <div className="kn-card p-4 space-y-4" data-testid="coupon-abuse-settings">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-extrabold text-slate-900">Anti-accaparramento coupon</p>
                  <p className="text-xs text-slate-500 mt-0.5">Blocca chi scarica offerte a numero chiuso senza usarle.</p>
                </div>
                <label className="inline-flex items-center gap-2 shrink-0">
                  <span className="text-sm font-bold text-slate-600">{couponAbuse.settings.enabled ? "Attivo" : "Disattivo"}</span>
                  <Switch checked={!!couponAbuse.settings.enabled} data-testid="coupon-abuse-enabled"
                    onCheckedChange={(v) => saveCouponAbuse({ enabled: v })} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Coupon scaduti tollerati</Label>
                  <Input type="number" min="0" data-testid="coupon-abuse-threshold" defaultValue={couponAbuse.settings.waste_threshold}
                    key={`th-${couponAbuse.settings.waste_threshold}`}
                    onBlur={(e) => { const n = Number(e.target.value); if (n !== couponAbuse.settings.waste_threshold) saveCouponAbuse({ waste_threshold: n }); }}
                    className="h-11 rounded-xl" />
                  <p className="text-[11px] text-slate-400">Oltre questo numero di coupon a numero chiuso scaduti senza uso scatta la sospensione.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Durata sospensione (ore)</Label>
                  <Input type="number" min="1" data-testid="coupon-abuse-hours" defaultValue={couponAbuse.settings.suspension_hours}
                    key={`hr-${couponAbuse.settings.suspension_hours}`}
                    onBlur={(e) => { const n = Number(e.target.value); if (n !== couponAbuse.settings.suspension_hours) saveCouponAbuse({ suspension_hours: n }); }}
                    className="h-11 rounded-xl" />
                  <p className="text-[11px] text-slate-400">Quanto dura il blocco delle richieste a numero chiuso.</p>
                </div>
              </div>
            </div>

            <div>
              <p className="font-extrabold text-slate-800 mb-2 flex items-center gap-1.5"><Ban className="h-4 w-4 text-amber-600" /> Clienti sospesi / segnalati ({couponAbuse.clients.length})</p>
              {couponAbuse.clients.length === 0 && <Empty text="Nessun cliente sospeso" />}
              <div className="space-y-2">
                {couponAbuse.clients.map((c) => (
                  <div key={c.client_account_id} className="kn-card p-3.5 flex items-center gap-3" data-testid={`coupon-abuse-client-${c.client_account_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-800 truncate flex items-center gap-2">
                        {c.name || c.email}
                        {c.currently_suspended
                          ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Sospeso</span>
                          : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Libero</span>}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{c.email} · sospensioni totali: <b className="text-slate-600">{c.suspension_count}</b></p>
                    </div>
                    {c.currently_suspended && (
                      <button onClick={() => revokeCouponSuspension(c.client_account_id)} data-testid={`coupon-revoke-${c.client_account_id}`}
                        className="h-9 px-3 rounded-xl bg-emerald-600 text-white font-bold text-sm shrink-0">Revoca</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="amount-disputes" className="mt-4 space-y-4">
            <div className="kn-card px-4 py-3 flex items-center divide-x divide-slate-100" data-testid="amount-stats-totals">
              <div className="flex-1 text-center"><p className="text-xl font-extrabold text-slate-900 leading-none">{amountStats.totals?.edits || 0}</p><p className="text-[11px] text-slate-500 mt-1">Modifiche</p></div>
              <div className="flex-1 text-center"><p className="text-xl font-extrabold text-slate-900 leading-none">{amountStats.totals?.disputes || 0}</p><p className="text-[11px] text-slate-500 mt-1">Contestazioni</p></div>
              <div className="flex-1 text-center"><p className="text-xl font-extrabold text-slate-900 leading-none">{amountStats.totals?.resolved || 0}</p><p className="text-[11px] text-slate-500 mt-1">Risolte</p></div>
            </div>

            <div className="inline-flex rounded-xl bg-slate-100 p-1" data-testid="dispute-view-toggle">
              <button onClick={() => setDisputeView("open")} data-testid="dispute-view-open" className={`h-9 px-4 rounded-lg text-sm font-bold ${disputeView === "open" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Aperte ({amountDisputes.length})</button>
              <button onClick={() => setDisputeView("resolved")} data-testid="dispute-view-resolved" className={`h-9 px-4 rounded-lg text-sm font-bold ${disputeView === "resolved" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Storico ({resolvedDisputes.length})</button>
            </div>

            {disputeView === "open" ? (
              <div>
                <p className="font-extrabold text-slate-800 mb-2 flex items-center gap-1.5"><Flag className="h-4 w-4 text-[#DC2626]" /> Contestazioni da decidere ({amountDisputes.length})</p>
                {amountDisputes.length === 0 && <Empty text="Nessuna contestazione aperta" />}
                <div className="space-y-2">
                  {amountDisputes.map((d) => (
                    <div key={d.claim_id} className="kn-card p-3.5" data-testid={`amount-dispute-${d.claim_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">{d.title || "Coupon"}</p>
                          <p className="text-xs text-slate-400 truncate">{d.store_name} · {d.client_name}</p>
                        </div>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-[#DC2626] shrink-0">Rifiutato da {d.disputed_by === "vendor" ? "negozio" : "cliente"}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="rounded-xl bg-slate-50 p-2.5">
                          <p className="text-[11px] text-slate-400 font-bold">Importo negozio</p>
                          <p className="font-extrabold text-slate-900">{d.vendor_amount_spent != null ? `${Number(d.vendor_amount_spent).toFixed(2)} €` : "—"}</p>
                          <button onClick={() => openResolve(d, d.vendor_amount_spent)} disabled={d.vendor_amount_spent == null} data-testid={`amount-use-vendor-${d.claim_id}`} className="mt-1 text-xs font-bold text-[#E63946] disabled:opacity-40">Usa questo</button>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-2.5">
                          <p className="text-[11px] text-slate-400 font-bold">Importo cliente</p>
                          <p className="font-extrabold text-slate-900">{d.client_amount_spent != null ? `${Number(d.client_amount_spent).toFixed(2)} €` : "—"}</p>
                          <button onClick={() => openResolve(d, d.client_amount_spent)} disabled={d.client_amount_spent == null} data-testid={`amount-use-client-${d.claim_id}`} className="mt-1 text-xs font-bold text-[#E63946] disabled:opacity-40">Usa questo</button>
                        </div>
                      </div>
                      {(d.receipts || []).length > 0 && (
                        <div className="mt-2.5">
                          <p className="text-[11px] text-slate-400 font-bold mb-1">Scontrini / fatture allegati</p>
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {d.receipts.map((r, i) => (
                              <button key={i} onClick={() => setReceiptView(r.url)} data-testid={`dispute-receipt-${d.claim_id}-${i}`} className="shrink-0 relative">
                                <img src={r.url} alt="scontrino" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                                <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] font-bold text-center rounded-b-lg py-0.5">{r.by === "vendor" ? "negozio" : "cliente"}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button onClick={() => openResolve(d)} data-testid={`amount-resolve-open-${d.claim_id}`} className="w-full h-10 rounded-xl font-bold mt-2">Decidi importo</Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div data-testid="resolved-disputes-list">
                <p className="font-extrabold text-slate-800 mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Contestazioni risolte ({resolvedDisputes.length})</p>
                {resolvedDisputes.length === 0 && <Empty text="Nessuna contestazione risolta" />}
                <div className="space-y-2">
                  {resolvedDisputes.map((d) => (
                    <div key={d.claim_id} className="kn-card p-3.5" data-testid={`resolved-dispute-${d.claim_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">{d.title || "Coupon"}</p>
                          <p className="text-xs text-slate-400 truncate">{d.store_name} · {d.client_name}</p>
                        </div>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">Deciso: {Number(d.amount_spent ?? 0).toFixed(2)} €</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                        <span>Negozio: <b className={d.vendor_wrong ? "text-[#DC2626]" : "text-slate-700"}>{d.vendor_amount_spent != null ? `${Number(d.vendor_amount_spent).toFixed(2)} €` : "—"}{d.vendor_wrong ? " ✗" : ""}</b></span>
                        <span>Cliente: <b className={d.client_wrong ? "text-[#DC2626]" : "text-slate-700"}>{d.client_amount_spent != null ? `${Number(d.client_amount_spent).toFixed(2)} €` : "—"}{d.client_wrong ? " ✗" : ""}</b></span>
                        <span>Sconto: <b className="text-slate-700">{Number(d.discount_eur ?? 0).toFixed(2)} €</b></span>
                      </div>
                      {d.admin_note && <p className="text-xs text-slate-500 mt-1.5 italic">Nota: "{d.admin_note}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="kn-card p-4 space-y-3" data-testid="amount-abuse-settings">
              <div className="flex items-center justify-between">
                <div><p className="font-extrabold text-slate-800 flex items-center gap-1.5"><Ban className="h-4 w-4 text-[#DC2626]" /> Blocco recidivi</p>
                  <p className="text-xs text-slate-400">Sospende in automatico chi supera le soglie di conti sbagliati o contestazioni.</p></div>
                <Switch checked={!!abuseSettings.enabled} data-testid="amount-abuse-enabled" onCheckedChange={(v) => setAbuseSettings((s) => ({ ...s, enabled: v }))} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Conti sbagliati max</Label><Input type="number" min="0" value={abuseSettings.wrong_threshold} data-testid="amount-abuse-wrong" onChange={(e) => setAbuseSettings((s) => ({ ...s, wrong_threshold: Number(e.target.value) }))} className="h-10 rounded-xl" /></div>
                <div><Label className="text-xs">Contestazioni max</Label><Input type="number" min="0" value={abuseSettings.dispute_threshold} data-testid="amount-abuse-dispute" onChange={(e) => setAbuseSettings((s) => ({ ...s, dispute_threshold: Number(e.target.value) }))} className="h-10 rounded-xl" /></div>
                <div><Label className="text-xs">Sospensione (ore)</Label><Input type="number" min="1" value={abuseSettings.suspension_hours} data-testid="amount-abuse-hours" onChange={(e) => setAbuseSettings((s) => ({ ...s, suspension_hours: Number(e.target.value) }))} className="h-10 rounded-xl" /></div>
              </div>
              <Button onClick={saveAbuseSettings} disabled={savingAbuse} data-testid="amount-abuse-save" className="h-10 rounded-xl font-bold">{savingAbuse ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva impostazioni"}</Button>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={onlyWrong} data-testid="amount-only-wrong" onCheckedChange={setOnlyWrong} />
              <Label className="text-sm text-slate-600">Mostra solo chi ha conti sbagliati</Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-extrabold text-slate-800 mb-2 flex items-center gap-1.5"><Store className="h-4 w-4 text-slate-500" /> Venditori sotto controllo</p>
                {(amountStats.vendors || []).filter((v) => !onlyWrong || v.wrong > 0).length === 0 && <Empty text="Nessun dato" />}
                <div className="space-y-2">
                  {(amountStats.vendors || []).filter((v) => !onlyWrong || v.wrong > 0).slice(0, 50).map((v) => (
                    <div key={v.vendor_id} className="kn-card p-3 flex items-center gap-3" data-testid={`amount-stat-vendor-${v.vendor_id}`}>
                      <div className="min-w-0 flex-1"><p className="font-bold text-slate-800 truncate flex items-center gap-1.5">{v.store_name || v.vendor_id}{v.currently_suspended && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-red-100 text-[#DC2626]" data-testid={`vendor-suspended-${v.vendor_id}`}>SOSPESO</span>}</p>
                        <p className="text-xs text-slate-400">Modifiche <b className="text-slate-600">{v.edits}</b> · Contestazioni <b className="text-slate-600">{v.disputes}</b> · Conti sbagliati <b className="text-[#DC2626]">{v.wrong}</b></p>
                      </div>
                      {v.currently_suspended && <button onClick={() => revokeAmountSuspension("vendor", v.vendor_id)} data-testid={`revoke-vendor-${v.vendor_id}`} className="text-xs font-bold text-[#E63946] shrink-0">Revoca</button>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-extrabold text-slate-800 mb-2 flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-500" /> Clienti sotto controllo</p>
                {(amountStats.clients || []).filter((c) => !onlyWrong || c.wrong > 0).length === 0 && <Empty text="Nessun dato" />}
                <div className="space-y-2">
                  {(amountStats.clients || []).filter((c) => !onlyWrong || c.wrong > 0).slice(0, 50).map((c) => (
                    <div key={c.client_account_id} className="kn-card p-3 flex items-center gap-3" data-testid={`amount-stat-client-${c.client_account_id}`}>
                      <div className="min-w-0 flex-1"><p className="font-bold text-slate-800 truncate flex items-center gap-1.5">{c.client_name || c.client_account_id}{c.currently_suspended && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-red-100 text-[#DC2626]" data-testid={`client-suspended-${c.client_account_id}`}>SOSPESO</span>}</p>
                        <p className="text-xs text-slate-400">Modifiche <b className="text-slate-600">{c.edits}</b> · Contestazioni <b className="text-slate-600">{c.disputes}</b> · Conti sbagliati <b className="text-[#DC2626]">{c.wrong}</b></p>
                      </div>
                      {c.currently_suspended && <button onClick={() => revokeAmountSuspension("client", c.client_account_id)} data-testid={`revoke-client-${c.client_account_id}`} className="text-xs font-bold text-[#E63946] shrink-0">Revoca</button>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>


          <TabsContent value="invites" className="mt-4 space-y-2">
            <div className="kn-card p-3.5 flex items-center gap-3" data-testid="admin-invites-summary">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800">{invitesConverted} negozi invitati si sono iscritti</p>
                <p className="text-xs text-slate-400">su {invites.length} richiesti dai clienti · crescita organica</p>
              </div>
            </div>
            {invites.length === 0 && <Empty text="Nessun negozio richiesto" />}
            {invites.map((iv) => {
              const subject = `KoupoNow — ${iv.unique_clients || iv.count} clienti ti cercano su KoupoNow`;
              const bodyLines = [
                `Ciao ${iv.name},`,
                ``,
                `${iv.unique_clients || iv.count} clienti della tua zona ti hanno cercato su KoupoNow, l'app dei coupon dei negozi vicini.`,
                `Ti piacerebbe pubblicare le tue offerte e raggiungerli? La registrazione è gratuita.`,
                ``,
                iv.address ? `Indirizzo: ${iv.address}` : "",
                iv.phone ? `Telefono: ${iv.phone}` : "",
                iv.website ? `Sito: ${iv.website}` : "",
                ``,
                `A presto,`,
                `Il team KoupoNow`,
              ].filter((l) => l !== null);
              const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
              return (
                <div key={iv.place_id} className={`kn-card p-3.5 flex items-center gap-3 ${iv.on_kn ? "ring-1 ring-emerald-200" : ""}`} data-testid={`admin-invite-${iv.place_id}`}>
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 font-extrabold ${iv.on_kn ? "bg-emerald-50 text-emerald-600" : "bg-[#FFE4E6] text-[#E63946]"}`}>{iv.count}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-800 truncate flex items-center gap-1.5">
                      {iv.name}
                      {iv.on_kn && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">Iscritto ✓</span>}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {iv.on_kn && iv.converted_store_name ? `Ora su KoupoNow come "${iv.converted_store_name}"` : (iv.address || iv.phone || iv.website)}
                    </p>
                  </div>
                  {iv.reminder_sent && !iv.on_kn && <span className="text-[11px] font-bold text-amber-600 shrink-0 hidden sm:flex items-center gap-1" title="Promemoria inviato"><BellRing className="h-3.5 w-3.5" /> promemoria</span>}
                  <span className="text-xs text-slate-400 shrink-0 hidden sm:block">{iv.unique_clients} richieste</span>
                  {!iv.on_kn && (
                    <a href={mailto} data-testid={`admin-invite-contact-${iv.place_id}`} className="shrink-0 h-9 px-3 rounded-lg bg-slate-900 text-white font-bold text-xs flex items-center gap-1.5 hover:bg-slate-800">
                      <Mail className="h-4 w-4" /> Contatta
                    </a>
                  )}
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="categories" className="mt-4 space-y-2">
            <p className="text-xs text-slate-500 px-1">Categorie richieste dai negozi (provvisorie, in attesa). Approva per renderle definitive, oppure rifiuta: le offerte collegate torneranno in "Altro".</p>
            {catRequests.length === 0 && <Empty text="Nessuna richiesta di categoria" />}
            {catRequests.map((req) => {
              const e = catEdits[req.request_id] || {};
              return (
                <div key={req.request_id} className="kn-card p-3.5 space-y-2.5" data-testid={`admin-catreq-${req.request_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><Tag className="h-4 w-4 text-amber-600" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-800 text-sm">«{req.name}»</p>
                      <p className="text-[11px] text-slate-400">Richiesta da {req.store_name || req.vendor_id} · {new Date(req.created_at).toLocaleDateString("it-IT")}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      data-testid={`admin-catreq-label-${req.request_id}`}
                      defaultValue={req.name}
                      onChange={(ev) => setCatEdits((p) => ({ ...p, [req.request_id]: { ...p[req.request_id], label: ev.target.value } }))}
                      placeholder="Nome categoria" className="h-10 rounded-xl flex-1" />
                    <Select value={e.macro_id || "altro"} onValueChange={(v) => setCatEdits((p) => ({ ...p, [req.request_id]: { ...p[req.request_id], macro_id: v } }))}>
                      <SelectTrigger data-testid={`admin-catreq-macro-${req.request_id}`} className="h-10 rounded-xl sm:w-[200px]"><SelectValue placeholder="Macro-categoria" /></SelectTrigger>
                      <SelectContent className="max-h-72">{catMacros.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`admin-catreq-approve-${req.request_id}`} onClick={() => approveCat(req)} className="flex-1 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold">Approva</Button>
                    <Button data-testid={`admin-catreq-reject-${req.request_id}`} onClick={() => rejectCat(req)} variant="outline" className="flex-1 h-10 rounded-xl font-bold text-rose-600 border-rose-200 hover:bg-rose-50">Rifiuta</Button>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="audit" className="mt-4 space-y-2">
            {auditLog.length === 0 && <Empty text="Nessuna azione registrata" />}
            {auditLog.map((l) => (
              <div key={l.log_id} className="kn-card p-3.5 flex items-start gap-3" data-testid={`admin-audit-${l.log_id}`}>
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><ScrollText className="h-4 w-4 text-slate-500" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 text-sm">{AUDIT_LABELS[l.action] || l.action}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{l.detail}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{l.admin_email} · {new Date(l.created_at).toLocaleString("it-IT")}</p>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="deleted" className="mt-4 space-y-2">
            <p className="text-xs font-bold text-slate-500 px-1 pt-1">Profili cancellati dall'utente (dati conservati per ricontatto)</p>
            {deletedProfiles.length === 0 && <Empty text="Nessun profilo cancellato" />}
            {deletedProfiles.map((p, i) => (
              <div key={i} className="kn-card p-3.5 flex items-start gap-3" data-testid={`admin-deleted-profile-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0"><Trash2 className="h-4 w-4 text-orange-500" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 truncate">{p.name || p.email} <span className="text-[11px] font-bold text-slate-400">· {p.type === "vendor" ? "negozio" : "cliente"}</span></p>
                  <p className="text-xs text-slate-500 truncate">{p.email}{p.phone ? ` · ${p.phone}` : ""}{p.city ? ` · ${p.city}` : ""}</p>
                  {p.reason && <p className="text-xs text-orange-700 mt-1 bg-orange-50 rounded-lg px-2 py-1">Motivo: {p.reason}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">{p.deleted_at ? new Date(p.deleted_at).toLocaleString("it-IT") : ""}</p>
                </div>
                <a href={`mailto:${p.email}`} data-testid={`admin-recontact-${i}`} className="shrink-0 h-9 px-3 rounded-lg bg-slate-900 text-white font-bold text-xs flex items-center gap-1.5 hover:bg-slate-800"><Mail className="h-4 w-4" /> Contatta</a>
              </div>
            ))}
            <p className="text-xs font-bold text-slate-500 px-1 pt-3">Email bloccate dall'admin (no ri-registrazione)</p>
            {deletedAccounts.length === 0 && <Empty text="Nessun account eliminato" />}
            {deletedAccounts.map((a) => (
              <div key={a.email} className="kn-card p-3.5 flex items-center gap-3" data-testid={`admin-deleted-${a.email}`}>
                <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="h-4 w-4 text-red-500" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 truncate">{a.name || a.email}</p>
                  <p className="text-xs text-slate-400 truncate">{a.email} · {a.type === "vendor" ? "negozio" : "cliente"} · eliminato {a.deleted_at ? new Date(a.deleted_at).toLocaleDateString("it-IT") : ""}</p>
                  {a.reason ? <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid={`admin-deleted-reason-${a.email}`}><span className="font-semibold">Motivo:</span> {a.reason}</p> : null}
                </div>
                <button onClick={() => restoreAccount(a.email)} data-testid={`admin-restore-${a.email}`} className="shrink-0 h-9 px-3 rounded-lg bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5 hover:bg-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> Sblocca
                </button>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="verifications" className="mt-4 space-y-2">
            <p className="text-xs text-slate-400 px-1">Verifica identità venditori. "Da verificare" = casi passati dall'AI per un controllo umano.</p>
            {verifications.length === 0 && <Empty text="Nessuna verifica in sospeso" />}
            {verifications.map((vf) => {
              const badge = { submitted: { l: "Da verificare", c: "bg-amber-50 text-amber-700" }, rejected: { l: "Rifiutata", c: "bg-red-50 text-red-600" }, unverified: { l: "Non inviata", c: "bg-slate-100 text-slate-500" } }[vf.status] || { l: vf.status, c: "bg-slate-100" };
              return (
                <div key={vf.vendor_id} className="kn-card p-3.5" data-testid={`admin-verif-${vf.vendor_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-800">{vf.company?.ragione_sociale || vf.store_name || vf.email}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${badge.c}`}>{badge.l}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{vf.email} · P.IVA {vf.company?.partita_iva || "—"} · Titolare {vf.owner?.nome} {vf.owner?.cognome}</p>
                  {vf.company?.sede && <p className="text-xs text-slate-400 mt-0.5">{vf.company.sede.indirizzo}, {vf.company.sede.citta} {vf.company.sede.provincia}</p>}
                  {vf.ai_result && <p className="text-xs text-slate-500 mt-1.5 bg-slate-50 rounded-lg p-2">🤖 AI: {vf.ai_result.decision} (conf. {Math.round((vf.ai_result.confidence || 0) * 100)}%) — {vf.ai_result.reasons}</p>}
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {(vf.documents || []).map((d) => (
                      <button key={d.type} onClick={() => viewDoc(vf.vendor_id, d.type)} data-testid={`admin-verif-doc-${vf.vendor_id}-${d.type}`} className="h-8 px-3 rounded-lg bg-slate-100 text-slate-700 font-bold text-xs flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" /> {d.type === "business" ? "Visura" : d.type === "id_front" ? "Doc fronte" : "Doc retro"}
                      </button>
                    ))}
                    <button onClick={() => approveVerif(vf.vendor_id)} data-testid={`admin-verif-approve-${vf.vendor_id}`} className="h-8 px-3 rounded-lg bg-emerald-600 text-white font-bold text-xs ml-auto flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                    </button>
                    <button onClick={() => rejectVerif(vf.vendor_id)} data-testid={`admin-verif-reject-${vf.vendor_id}`} className="h-8 px-3 rounded-lg bg-red-50 text-red-600 font-bold text-xs flex items-center gap-1.5">
                      <Ban className="h-3.5 w-3.5" /> Rifiuta
                    </button>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="app" className="mt-4 space-y-4">
            <div className="kn-card p-4 space-y-4" data-testid="pwa-settings">
              <div className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-[#E63946]" />
                <div>
                  <p className="font-extrabold text-slate-900">Icona dell'app (schermata Home)</p>
                  <p className="text-xs text-slate-500 mt-0.5">L'immagine che vedono gli utenti quando installano KoupoNow sul telefono.</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <img
                  key={pwaIconKey}
                  src={`${API}/pwa/icon?k=${pwaIconKey}&v=${pwa.version}`}
                  alt="Icona app"
                  data-testid="pwa-icon-preview"
                  className="h-20 w-20 rounded-2xl border border-slate-200 object-cover bg-slate-50 shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-700">
                    {pwa.has_custom ? "Icona personalizzata" : "Icona predefinita"}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Consigliato: PNG quadrato 512×512px. Max 5MB.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <label
                  data-testid="pwa-upload-label"
                  className={`h-11 px-4 rounded-xl bg-slate-900 text-white font-bold text-sm inline-flex items-center gap-2 cursor-pointer ${pwaUploading ? "opacity-60 pointer-events-none" : "hover:bg-slate-800"}`}
                >
                  {pwaUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {pwaUploading ? "Caricamento…" : "Carica icona"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="pwa-upload-input"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadPwaIcon(f); }}
                  />
                </label>
                {pwa.has_custom && (
                  <button
                    onClick={resetPwaIcon}
                    data-testid="pwa-reset-btn"
                    className="h-11 px-4 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm inline-flex items-center gap-2 hover:bg-slate-200"
                  >
                    <RotateCcw className="h-4 w-4" /> Ripristina predefinita
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-400">Le modifiche sono immediate per i nuovi utenti. Chi ha già installato l'app vedrà l'icona aggiornata dopo la reinstallazione.</p>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-offer-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Nuova offerta di test</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label>Negozio</Label>
              <Select value={offerForm.vendor_id} onValueChange={(v) => setOfferForm({ ...offerForm, vendor_id: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="admin-offer-vendor"><SelectValue placeholder="Scegli il negozio" /></SelectTrigger>
                <SelectContent className="max-h-72">{vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.store_name || v.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Titolo</Label><Input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} placeholder="Es. Sconto 20% di test" className="h-11 rounded-xl" data-testid="admin-offer-title" /></div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={offerForm.category} onValueChange={(v) => setOfferForm({ ...offerForm, category: v })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="admin-offer-category"><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                <SelectContent className="max-h-72">{allTags.map((t) => <SelectItem key={t.id} value={t.id}>{t.label} · {t.macro}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo sconto</Label>
                <Select value={offerForm.discount_type} onValueChange={(v) => setOfferForm({ ...offerForm, discount_type: v })}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="admin-offer-dtype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Sconto %</SelectItem>
                    <SelectItem value="fixed">Importo € fisso</SelectItem>
                    <SelectItem value="price">Prezzo speciale</SelectItem>
                    <SelectItem value="bogo">2x1 / Omaggio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Valore</Label><Input type="number" min="0" value={offerForm.discount_value} disabled={offerForm.discount_type === "bogo"} onChange={(e) => setOfferForm({ ...offerForm, discount_value: e.target.value })} className="h-11 rounded-xl" data-testid="admin-offer-dvalue" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Quantità (opz.)</Label><Input type="number" min="1" value={offerForm.quantity} onChange={(e) => setOfferForm({ ...offerForm, quantity: e.target.value })} placeholder="Illimitata" className="h-11 rounded-xl" data-testid="admin-offer-qty" /></div>
              <div className="space-y-1.5">
                <Label>Durata</Label>
                <Select value={offerForm.duration} onValueChange={(v) => setOfferForm({ ...offerForm, duration: v })}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="admin-offer-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Solo oggi</SelectItem>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={createOffer} disabled={savingOffer} data-testid="admin-offer-save" className="w-full h-12 rounded-xl font-bold">
              {savingOffer ? <Loader2 className="h-5 w-5 animate-spin" /> : "Crea offerta"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(v) => { if (!v && !delBusy) setDelTarget(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold">
              Elimina {delTarget?.type === "vendors" ? "negozio" : "cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              Stai per eliminare <b>{delTarget?.title}</b>. L'azione è <b>irreversibile</b>: l'account e i dati collegati verranno rimossi e l'utente riceverà un'email con il motivo.
            </div>
            <div className="space-y-1.5">
              <Label>Motivo dell'eliminazione</Label>
              <Select value={delReason} onValueChange={setDelReason}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="delete-reason-select"><SelectValue placeholder="Seleziona un motivo" /></SelectTrigger>
                <SelectContent>
                  {["Violazione dei termini di servizio", "Comportamento scorretto o abusi", "Account falso o sospetto", "Attività fraudolenta", "Contenuti inappropriati", "Richiesta dell'utente", "Altro"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{delReason === "Altro" ? "Specifica il motivo" : "Nota aggiuntiva (facoltativa)"}</Label>
              <Textarea value={delNote} onChange={(e) => setDelNote(e.target.value)} rows={3}
                placeholder={delReason === "Altro" ? "Scrivi il motivo che verrà inviato all'utente" : "Dettagli opzionali inclusi nell'email"}
                className="rounded-xl" data-testid="delete-reason-note" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setDelTarget(null)} disabled={delBusy} data-testid="delete-cancel-btn" className="flex-1 h-11 rounded-xl font-bold">Annulla</Button>
              <Button onClick={confirmDelete}
                disabled={delBusy || !delReason || (delReason === "Altro" && !delNote.trim())}
                data-testid="delete-confirm-btn" className="flex-1 h-11 rounded-xl font-bold bg-[#DC2626] hover:bg-[#b91c1c]">
                {delBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Elimina"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={!!demoType} onOpenChange={(v) => { if (!v) { setDemoType(null); setDemoCreds(null); setDemoForm(emptyDemo); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="admin-demo-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">{demoCreds ? "Account demo creato" : demoType === "vendor" ? "Nuovo negozio demo" : "Nuovo cliente demo"}</DialogTitle></DialogHeader>
          {demoCreds ? (
            <div className="space-y-3.5" data-testid="demo-creds">
              <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 className="h-5 w-5" /> {demoCreds.type === "vendor" ? "Negozio" : "Cliente"} pronto</div>
              <p className="text-sm text-slate-500">Usa queste credenziali per accedere{demoCreds.type === "vendor" ? " dall'area negozio (/vendor/login)" : " dall'app cliente"}. Salvale ora: la password non sarà più visibile.</p>
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-bold text-slate-400 uppercase">Email</span>
                  <span className="font-mono text-sm font-semibold text-slate-800 truncate" data-testid="demo-creds-email">{demoCreds.email}</span>
                </div>
                <div className="h-px bg-slate-200" />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-bold text-slate-400 uppercase">Password</span>
                  <span className="font-mono text-sm font-semibold text-slate-800" data-testid="demo-creds-password">{demoCreds.password}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyCreds} data-testid="demo-creds-copy" className="flex-1 h-11 rounded-xl font-bold gap-2"><Copy className="h-4 w-4" /> Copia</Button>
                <Button onClick={() => { setDemoType(null); setDemoCreds(null); setDemoForm(emptyDemo); }} data-testid="demo-creds-done" className="flex-1 h-11 rounded-xl font-bold">Fatto</Button>
              </div>
            </div>
          ) : (
          <div className="space-y-3.5">
            {demoType === "vendor" ? (
              <>
                <div className="space-y-1.5"><Label>Nome negozio</Label><Input value={demoForm.store_name} onChange={(e) => setDemoForm({ ...demoForm, store_name: e.target.value })} placeholder="Es. Bar Demo" className="h-11 rounded-xl" data-testid="demo-store-name" /></div>
                <div className="space-y-1.5">
                  <Label>Categoria</Label>
                  <Select value={demoForm.category} onValueChange={(v) => setDemoForm({ ...demoForm, category: v })}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="demo-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">{allTags.map((t) => <SelectItem key={t.id} value={t.id}>{t.label} · {t.macro}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Nome cliente</Label><Input value={demoForm.name} onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} placeholder="Es. Mario Demo" className="h-11 rounded-xl" data-testid="demo-name" /></div>
                <div className="space-y-1.5"><Label>Telefono (opz.)</Label><Input value={demoForm.phone} onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })} className="h-11 rounded-xl" data-testid="demo-phone" /></div>
              </>
            )}
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" autoCapitalize="none" autoCorrect="off" value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} placeholder="demo@esempio.it" className="h-11 rounded-xl" data-testid="demo-email" /></div>
            <div className="space-y-1.5"><Label>Password (opz.)</Label><Input value={demoForm.password} onChange={(e) => setDemoForm({ ...demoForm, password: e.target.value })} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="Predefinita: password123" className="h-11 rounded-xl" data-testid="demo-password" /></div>
            <Button onClick={createDemo} disabled={savingDemo} data-testid="demo-save" className="w-full h-12 rounded-xl font-bold">
              {savingDemo ? <Loader2 className="h-5 w-5 animate-spin" /> : "Crea account demo"}
            </Button>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {detailUser && (
        <AdminUserDetail type={detailUser.type} id={detailUser.id} onClose={() => setDetailUser(null)} onChanged={load} />
      )}

      <Dialog open={!!rejectVerifId} onOpenChange={(v) => !v && setRejectVerifId(null)}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="admin-verif-reject-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Rifiuta verifica</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label>Motivo (inviato via email al venditore)</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Es. La visura è illeggibile, ricaricala più nitida." className="rounded-xl min-h-[90px]" data-testid="reject-reason-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Documenti da reinviare (opzionale)</Label>
              <div className="space-y-1.5">
                {[{ t: "business", l: "Visura / documento aziendale" }, { t: "id_front", l: "Documento titolare (fronte)" }, { t: "id_back", l: "Documento titolare (retro)" }].map((d) => (
                  <label key={d.t} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" data-testid={`reject-doc-${d.t}`} checked={rejectDocs.includes(d.t)}
                      onChange={(e) => setRejectDocs((s) => e.target.checked ? [...s, d.t] : s.filter((x) => x !== d.t))}
                      className="h-4 w-4 rounded accent-[#E63946]" />
                    {d.l}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRejectVerifId(null)} className="flex-1 h-11 rounded-xl font-bold">Annulla</Button>
              <Button onClick={confirmRejectVerif} data-testid="reject-confirm-btn" className="flex-1 h-11 rounded-xl font-bold bg-[#DC2626] hover:bg-[#b91c1c]">Rifiuta</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveTarget} onOpenChange={(v) => { if (!v && !resolvingBusy) setResolveTarget(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="amount-resolve-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Decidi l'importo</DialogTitle><DialogDescription>Stabilisci l'importo speso definitivo per questa contestazione.</DialogDescription></DialogHeader>
          {resolveTarget && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{resolveTarget.store_name} · {resolveTarget.client_name} — "{resolveTarget.title}"</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl bg-slate-50 p-2.5"><span className="text-slate-400 text-xs font-bold">Negozio</span><p className="font-extrabold">{resolveTarget.vendor_amount_spent != null ? `${Number(resolveTarget.vendor_amount_spent).toFixed(2)} €` : "—"}</p></div>
                <div className="rounded-xl bg-slate-50 p-2.5"><span className="text-slate-400 text-xs font-bold">Cliente</span><p className="font-extrabold">{resolveTarget.client_amount_spent != null ? `${Number(resolveTarget.client_amount_spent).toFixed(2)} €` : "—"}</p></div>
              </div>
              <div className="space-y-1.5"><Label>Importo speso definitivo (€)</Label>
                <Input type="number" min="0" step="0.01" value={resolveForm.amount_spent} data-testid="amount-resolve-spent"
                  onChange={(e) => setResolveForm((f) => ({ ...f, amount_spent: e.target.value }))} className="h-11 rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Sconto (€)</Label>
                <Input type="number" min="0" step="0.01" value={resolveForm.discount_eur} data-testid="amount-resolve-discount"
                  onChange={(e) => setResolveForm((f) => ({ ...f, discount_eur: e.target.value }))} className="h-11 rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Nota (facoltativa)</Label>
                <Textarea value={resolveForm.note} data-testid="amount-resolve-note"
                  onChange={(e) => setResolveForm((f) => ({ ...f, note: e.target.value }))} className="rounded-xl" rows={2} /></div>
              <Button onClick={submitResolve} disabled={resolvingBusy} data-testid="amount-resolve-submit" className="w-full h-11 rounded-xl font-bold">
                {resolvingBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Conferma decisione"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {receiptView && (
        <div onClick={() => setReceiptView(null)} data-testid="receipt-viewer"
          className="fixed inset-0 z-[4000] bg-black/85 flex items-center justify-center p-4">
          <img src={receiptView} alt="scontrino" className="max-h-[90vh] max-w-full rounded-xl" />
          <button onClick={() => setReceiptView(null)} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

const AUDIT_LABELS = {
  vendor_create: "Negozio demo creato",
  client_create: "Cliente demo creato",
  vendor_delete: "Negozio eliminato",
  client_delete: "Cliente eliminato",
  vendor_block: "Negozio sospeso",
  vendor_unblock: "Negozio riattivato",
  client_block: "Cliente sospeso",
  client_unblock: "Cliente riattivato",
  vendor_reset_password: "Password negozio reimpostata",
  client_reset_password: "Password cliente reimpostata",
  offer_delete: "Offerta eliminata",
  review_delete: "Recensione eliminata",
  backup_download: "Backup DB scaricato",
  account_restore: "Ri-registrazione sbloccata",
  vendor_verify_approve: "Verifica venditore approvata",
  vendor_verify_reject: "Verifica venditore rifiutata",
};

const REPORT_LABELS = {
  offerta_non_rispettata: "Offerta non rispettata / falsa",
  coupon_rifiutato: "Coupon rifiutato dal negozio",
  comportamento_scorretto: "Comportamento scorretto",
  prezzo_diverso: "Prezzo diverso da quello pubblicizzato",
  altro: "Altro",
};

function Empty({ text }) {
  return <div className="kn-card p-8 text-center text-slate-400">{text}</div>;
}

function Row({ title, code, sub, blocked, onOpen, onBlock, onDelete, testid }) {
  return (
    <div className="kn-card p-3.5 flex items-center gap-3" data-testid={testid}>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`${testid}-open`}>
        <p className="font-bold text-slate-800 truncate flex items-center gap-2">
          {title}
          {code && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FFE4E6] text-[#BE123C] shrink-0">{code}</span>}
          {blocked && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Sospeso</span>}
        </p>
        <p className="text-xs text-slate-400 truncate">{sub}</p>
      </button>
      <button onClick={onBlock} title={blocked ? "Riattiva" : "Sospendi"} className={`h-9 w-9 rounded-lg flex items-center justify-center ${blocked ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`} data-testid={`${testid}-block`}>
        {blocked ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
      </button>
      <button onClick={onDelete} title="Elimina" className="h-9 w-9 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50" data-testid={`${testid}-delete`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}
